import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../api/client';
import { toast } from 'sonner';

const TRADES = [
  { id: 'plumbing', label: 'Plumbing', icon: '🔧' },
  { id: 'electrical', label: 'Electrical', icon: '⚡' },
  { id: 'hvac', label: 'HVAC', icon: '❄️' },
  { id: 'handyman', label: 'Handyman', icon: '🔨' },
  { id: 'landscaping', label: 'Landscaping', icon: '🌿' },
  { id: 'cleaning', label: 'Cleaning', icon: '✨' },
];

export default function ContractorOnboarding() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: '', display_name: '', bio: '', trade_category_id: '', hourly_rate: '', grazing_territory_miles: 25, home_base_zip: '', insurance_status: 'unverified' as const, years_experience: '' });
  const [loading, setLoading] = useState(false);

  const update = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!form.business_name || !form.display_name || !form.trade_category_id || !form.home_base_zip) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const result = await apiCall('/profiles/contractor', {
        method: 'POST',
        body: JSON.stringify({ ...form, hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : undefined, years_experience: form.years_experience ? parseInt(form.years_experience) : undefined, terms_accepted: true }),
      });
      if (result.success) {
        toast.success('Profile submitted! Pending admin approval.');
        navigate('/contractor-dashboard');
      } else { toast.error(result.error || 'Failed'); }
    } catch { toast.error('Something went wrong'); }
    finally { setLoading(false); }
  };

  return (
    <div className="page" style={{ maxWidth: 560, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>Contractor Signup</h1>
      <p style={{ opacity: 0.7, marginBottom: 32, textAlign: 'center' }}>Tell us about your business. Your profile will be reviewed before going live.</p>

      <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Business Name *</label>
      <input type="text" value={form.business_name} onChange={e => update('business_name', e.target.value)} placeholder="e.g. Smith Plumbing LLC" style={{ width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', fontSize: '1rem', marginBottom: 16, background: 'var(--color-cream)' }} />

      <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Your Name *</label>
      <input type="text" value={form.display_name} onChange={e => update('display_name', e.target.value)} placeholder="e.g. John Smith" style={{ width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', fontSize: '1rem', marginBottom: 16, background: 'var(--color-cream)' }} />

      <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Your Trade *</label>
      <div className="chip-group" style={{ marginBottom: 16 }}>
        {TRADES.map(t => (
          <button key={t.id} className={`chip ${form.trade_category_id === t.id ? 'active' : ''}`} onClick={() => update('trade_category_id', t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Home Base Zip Code *</label>
      <input type="text" value={form.home_base_zip} onChange={e => update('home_base_zip', e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="22030" maxLength={5} style={{ width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', fontSize: '1rem', marginBottom: 16, background: 'var(--color-cream)' }} />

      <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Grazing Territory: {form.grazing_territory_miles} miles</label>
      <input type="range" className="range-slider" min={1} max={100} value={form.grazing_territory_miles} onChange={e => update('grazing_territory_miles', parseInt(e.target.value))} style={{ marginBottom: 16 }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Hourly Rate ($)</label>
          <input type="number" value={form.hourly_rate} onChange={e => update('hourly_rate', e.target.value)} placeholder="75" style={{ width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', fontSize: '1rem', background: 'var(--color-cream)' }} />
        </div>
        <div>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Years Experience</label>
          <input type="number" value={form.years_experience} onChange={e => update('years_experience', e.target.value)} placeholder="5" style={{ width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', fontSize: '1rem', background: 'var(--color-cream)' }} />
        </div>
      </div>

      <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Bio (optional)</label>
      <textarea value={form.bio} onChange={e => update('bio', e.target.value)} placeholder="Tell homeowners about your experience and specialties..." rows={3} style={{ width: '100%', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', fontSize: '1rem', marginBottom: 24, resize: 'vertical', background: 'var(--color-cream)' }} />

      <div className="disclaimer" style={{ marginBottom: 16 }}>By signing up, you accept the <a href="/contractor-agreement">Contractor Agreement</a>. Your profile will be reviewed by our team.</div>
      <button className="btn-primary" onClick={handleSubmit} disabled={loading} style={{ width: '100%' }}>
        {loading ? 'Submitting...' : 'Submit for Review →'}
      </button>
    </div>
  );
}
